import React, { useCallback, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { ExtratableRepoIcon } from "assets/material-custom-icons";
import { ClassificationPrompt } from "components/File-repository/Table/ClassificationPrompt";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook, usePrompt } from "hooks";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useAccountSelector, useDealActions } from "store";
import { useDateFormatSelect } from "store/selectors/user-selectors";
import { IFileRepo } from "store/types";

import { EXTRACTABLE_DOCUMENT_TYPES } from "../../../constants";

import { DataExtractionStatus } from "./DataExtractionStatus";

interface Props {
  fileData: IFileRepo | null;
  loading?: boolean;
  readOnly?: boolean;
  showExtractData?: boolean;
  onRerunExtractor?: () => void;
  extractorLoading?: boolean;
  onFileUpdated?: (updatedFile: IFileRepo) => void;
}

const AddValueButton: React.FC<{ onClick?: (e: React.MouseEvent<HTMLDivElement>) => void; disabled?: boolean }> = ({
  onClick,
  disabled
}) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px",
      borderRadius: "32px",
      cursor: disabled ? "default" : "pointer",
      transition: "padding 0.2s ease, background-color 0.2s ease",
      bgcolor: "#f5f6f7",
      opacity: disabled ? 0.5 : 1,
      "& .add-value-label": {
        maxWidth: 0,
        opacity: 0,
        overflow: "hidden",
        whiteSpace: "nowrap",
        fontSize: "12px",
        color: "#071f35",
        lineHeight: 1,
        transition: "max-width 0.2s ease, opacity 0.2s ease"
      },
      ...(!disabled && {
        "&:hover": {
          padding: "4px 8px",
          bgcolor: "rgba(7, 31, 53, 0.04)",
          "& .add-value-label": { maxWidth: "80px", opacity: 1 },
          "& .add-value-icon": { color: "#071f35" }
        }
      })
    }}
  >
    <AddIcon className="add-value-icon" sx={{ fontSize: 16, color: "#8e99a3" }} />
    <Box component="span" className="add-value-label">
      Add Value
    </Box>
  </Box>
);

const InfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  valueColor?: string;
  clickable?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}> = ({ icon, label, value, valueColor = "#071f35", clickable, onClick }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [value]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="8px"
      onClick={clickable && value ? onClick : undefined}
      sx={{ cursor: clickable && value ? "pointer" : "default" }}
    >
      <Stack direction="row" alignItems="center" gap="4px">
        {icon}
        <Typography noWrap sx={{ fontSize: "16px", fontWeight: 500, color: "#496682" }}>
          {label}
        </Typography>
      </Stack>
      {!value && clickable ? (
        <AddValueButton onClick={onClick} />
      ) : (
        <MaterialTooltip isShow title={copied ? "Copied" : value} disableHoverListener={!copied && value.length <= 20}>
          <Typography
            noWrap
            onClick={handleCopy}
            sx={{ fontSize: "16px", color: valueColor, maxWidth: "140px", cursor: "pointer" }}
          >
            {value}
          </Typography>
        </MaterialTooltip>
      )}
    </Stack>
  );
};

export const DataExtractionMetadata: React.FC<Props> = ({
  fileData,
  loading: fileLoading,
  readOnly,
  showExtractData = true,
  onRerunExtractor,
  extractorLoading,
  onFileUpdated
}) => {
  const dateFormat = useDateFormatSelect();
  const formatDateHook = useFormatDateHook();
  const openPrompt = usePrompt();
  const { show_recommendations } = useAccountSelector() || {};

  const { updateFileRepoAction } = useDealActions();
  const { action: patchFile } = useActionHook(updateFileRepoAction, {
    onSuccess: (data) => {
      onFileUpdated?.(data);
    }
  });

  const documentTypeIsExtractable = useMemo(() => {
    return EXTRACTABLE_DOCUMENT_TYPES.includes(fileData?.document_type ?? "");
  }, [fileData]);

  const handleClassificationClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    if (!fileData || readOnly) return;

    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: ClassificationPrompt,
      props: {
        currentClassification: fileData.document_type ?? null
      }
    });
    if (!result) return;
    await patchFile({ fileId: fileData.id, document_type: result.classification });
  };

  if (fileLoading || !fileData) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{ borderTop: "1px solid #dbe8f5", borderBottom: "1px solid #dbe8f5", py: "10px", height: "52px" }}
      >
        <CircularProgress size={20} />
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ borderTop: "1px solid #dbe8f5", borderBottom: "1px solid #dbe8f5", py: "9px", px: "16px" }}
    >
      <Stack direction="row" alignItems="center" gap="10px">
        <InfoItem
          icon={<CorporateFareOutlinedIcon sx={{ fontSize: 18, color: "#496682" }} />}
          label="Deal:"
          value={fileData.deal?.title || ""}
        />
        <InfoItem
          icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: "#496682" }} />}
          label="Add Date:"
          value={formatDateHook({ date: fileData.created_at, format: dateFormat })}
        />
        <InfoItem
          icon={<LabelOutlinedIcon sx={{ fontSize: 18, color: "#496682" }} />}
          label="Altius Classification:"
          value={fileData.document_type || ""}
          valueColor="#26a6ae"
          clickable={!readOnly}
          onClick={handleClassificationClick}
        />
        <DataExtractionStatus fileData={fileData} readOnly={readOnly} onFileUpdated={onFileUpdated} />
      </Stack>
      {!readOnly && showExtractData && onRerunExtractor && show_recommendations && (
        <MaterialTooltip
          variant="dark"
          isShow={!documentTypeIsExtractable}
          title={`Extraction is supported for ${EXTRACTABLE_DOCUMENT_TYPES.join(", ")} Classifications`}
        >
          <span>
            <Button
              disabled={extractorLoading || !documentTypeIsExtractable}
              onClick={onRerunExtractor}
              startIcon={
                <ExtratableRepoIcon
                  sx={{
                    width: 16,
                    height: 16,
                    fill: extractorLoading || !documentTypeIsExtractable ? "#7b92a9" : "#071F35"
                  }}
                />
              }
              sx={{
                borderRadius: "32px",
                border: "1px solid #7b92a9",
                bgcolor: "white",
                textTransform: "none",
                padding: "4px 8px",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "16px",
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#f6f8fc" },
                "&.Mui-disabled": {
                  color: "#7b92a9",
                  borderColor: "#dbe8f5",
                  bgcolor: "white"
                }
              }}
            >
              Rerun AI Extractor
            </Button>
          </span>
        </MaterialTooltip>
      )}
    </Stack>
  );
};
